import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Gem, Sparkles, Circle, Crown, Hammer, Coins,
  Users, Building2, ScrollText, FileText, Settings, LogOut, Menu, X, Inbox, Diamond, PencilLine, Scissors,
  Timer, AlertTriangle, PanelLeftClose, PanelLeftOpen, Component, Bell, PencilRuler, Cpu,
  CalendarClock, NotebookPen, HardDrive, LayoutGrid, Palette
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useNotifications } from '@/lib/notifications';
import { canSeeNavItem } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import CompanySwitcher from './CompanySwitcher';
import KeyboardShortcuts, { KeyboardShortcutsButton, KeyboardShortcutsDialog } from './KeyboardShortcuts';
import DensityToggle from './DensityToggle';
import CommandPalette from './CommandPalette';
import TeamPresencePanel from './TeamPresencePanel';
import inventiqueLogo from '@/assets/inventique-logo.png';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'sidebar-nav-dashboard-link' },
  { to: '/notifications', label: 'Notifications', icon: Bell, testid: 'sidebar-nav-notifications-link' },
  { to: '/gold', label: 'Gold', icon: Coins, testid: 'sidebar-nav-gold-link', color: 'var(--gold-icon)' },
  // Wave 3.99z — Mountings (Semi-Finished Gold Pieces) master. Grams-only
  // stock; issued weight is part of jewellery net weight but excluded
  // from karigar labour calculation.
  { to: '/mountings', label: 'Mountings', icon: Component, testid: 'sidebar-nav-mountings-link', color: 'var(--gold-accent, #B08D57)' },
  { to: '/diamonds', label: 'Diamonds', icon: Gem, testid: 'sidebar-nav-diamonds-link', color: 'var(--diamond-icon)' },
  { to: '/color-stones', label: 'Color Stones', icon: Sparkles, testid: 'sidebar-nav-color-stones-link', color: 'var(--colorstone-icon, #C84A6F)' },
  { to: '/pearls', label: 'Pearls', icon: Circle, testid: 'sidebar-nav-pearls-link', color: 'var(--pearl-icon, #6B7280)' },
  // Wave 4.07 — CAD Developers master. Sits between Pearls and Order Forms
  // per product owner (a design/production party used on the CAD step).
  { to: '/cad-developers', label: 'CAD Developers', icon: PencilRuler, testid: 'sidebar-nav-cad-developers-link', color: 'var(--accent-navy, #0b1b2b)' },
  { to: '/orders', label: 'Order Forms', icon: ScrollText, testid: 'sidebar-nav-orders-link' },
  { to: '/production-timeline', label: 'Production Timeline', icon: Timer, testid: 'sidebar-nav-production-timeline-link', indent: true, color: 'var(--gold-accent, #B08D57)' },
  { to: '/jewellery', label: 'Jewellery', icon: Diamond, testid: 'sidebar-nav-jewellery-link', color: 'var(--accent-gold, #B45309)' },
  { to: '/designs', label: 'Design Master', icon: Palette, testid: 'sidebar-nav-designs-link', color: 'var(--accent-navy, #0b1b2b)' },
  { to: '/vendors', label: 'Vendors', icon: Building2, testid: 'sidebar-nav-vendors-link' },
  { to: '/customers', label: 'Customers', icon: Users, testid: 'sidebar-nav-customers-link' },
  { to: '/goldsmiths', label: 'Goldsmiths', icon: Hammer, testid: 'sidebar-nav-goldsmiths-link', color: 'var(--goldsmith-icon)' },
  { to: '/cutters', label: 'Cutters', icon: Scissors, testid: 'sidebar-nav-cutters-link', color: 'var(--accent-navy, #0b1b2b)' },
  { to: '/cutters/ledger', label: 'Cutter Ledger', icon: Scissors, testid: 'sidebar-nav-cutter-ledger-link', indent: true },
  { to: '/broken-packet-issues', label: 'Broken Recut Issues', icon: Scissors, testid: 'sidebar-nav-broken-recut-link', indent: true },
  { to: '/reports', label: 'Reports', icon: FileText, testid: 'sidebar-nav-reports-link' },
  { to: '/reports/digest', label: 'Activity Digest', icon: CalendarClock, testid: 'sidebar-nav-digest-report-link', indent: true, color: 'var(--diamond-icon)' },
  { to: '/reports/memos', label: 'Memos', icon: FileText, testid: 'sidebar-nav-memos-link', indent: true },
  { to: '/reports/customer-gold-ledger', label: 'Customer Gold Report', icon: Coins, testid: 'sidebar-nav-customer-gold-report-link', indent: true, color: 'var(--gold-icon)' },
  // Wave 4.07 — CAD Development report (indented child under Reports).
  { to: '/reports/cad-development', label: 'CAD Development', icon: Cpu, testid: 'sidebar-nav-cad-report-link', indent: true, color: 'var(--accent-navy, #0b1b2b)' },
  { to: '/reports/production-stage-digest', label: 'Production Digest', icon: LayoutGrid, testid: 'sidebar-nav-production-digest-link', indent: true, color: 'var(--diamond-icon)' },
  { to: '/reports/estimated-delivery', label: 'Estimated Delivery', icon: CalendarClock, testid: 'sidebar-nav-estimated-delivery-link', indent: true, color: 'var(--gold-accent)' },
  { to: '/requests', label: 'Delete Requests', icon: Inbox, testid: 'sidebar-nav-requests-link' },
  { to: '/edit-requests', label: 'Edit Requests', icon: PencilLine, testid: 'sidebar-nav-edit-requests-link' },
  { to: '/logs', label: 'Activity Logs', icon: ScrollText, testid: 'sidebar-nav-logs-link' },
];

const ADMIN_NAV_FOR_ALL_ADMINS = [
  { to: '/admin/users', label: 'Users', icon: Users, testid: 'sidebar-nav-users-link' },
];
const SUPER_NAV = [
  { to: '/admin/companies', label: 'Companies', icon: Building2, testid: 'sidebar-nav-companies-link' },
  { to: '/admin/portal-access', label: 'Portal Access', icon: Settings, testid: 'sidebar-nav-portal-access-link' },
  { to: '/admin/storage', label: 'Storage Console', icon: HardDrive, testid: 'sidebar-nav-storage-console-link' },
  { to: '/admin/daily-digest', label: 'Daily Digest', icon: CalendarClock, testid: 'sidebar-nav-daily-digest-link' },
];

function SidebarContent({ onNav, onCollapse, showCollapseButton, mode = 'expanded' }) {
  const { user, logout, isSuperAdmin, hasPermission } = useAuth();
  const { inboxUnread } = useNotifications();
  const location = useLocation();
  // Wave 3.99h — Hide sidebar links the current user has no permission
  // to open. Super Admin and Company Admin naturally bypass because
  // ``hasPermission`` returns true for them (see lib/auth.jsx).
  const visibleNav = NAV.filter((n) => canSeeNavItem(user, n, hasPermission));
  // Wave 3.99y — Rail mode: 64-px icon-only strip. Labels drop to
  // ``title`` tooltips, indented children get the same treatment as
  // parents (indent is meaningless when there's no label). Admin
  // section header collapses to a subtle divider.
  const isRail = mode === 'rail';
  return (
    <div className="flex flex-col h-full">
      <div className={`${isRail ? 'px-1 py-2' : 'px-3 py-2'} border-b border-[var(--divider)] flex items-center justify-center relative`}>
        <img
          src={inventiqueLogo}
          alt="Inventique"
          className={
            isRail
              ? 'w-10 h-10 object-contain'
              : 'w-[82%] max-w-[210px] max-h-[150px] h-auto object-contain'
          }
          data-testid="app-brand-logo"
        />
        <div className="sr-only" data-testid="app-brand-title">Inventique</div>
        {/* Wave 3.99x/y — Collapse-cycle chevron. Cycles the sidebar
            through the three modes (expanded → rail → collapsed →
            expanded). Never rendered on mobile since the mobile
            sheet already dismisses on route change. */}
        {showCollapseButton && onCollapse && !isRail && (
          <button
            type="button"
            onClick={onCollapse}
            className="hidden md:inline-flex absolute right-1 top-1 text-slate-400 hover:text-slate-800 p-1.5 rounded-md hover:bg-slate-100"
            data-testid="sidebar-collapse-btn"
            title="Shrink to rail (Ctrl + \\)"
            aria-label="Shrink sidebar to rail"
          >
            <PanelLeftClose size={15} />
          </button>
        )}
      </div>
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-3 ${isRail ? 'px-1.5' : 'px-3'} space-y-0.5`}>
        {visibleNav.map((n) => {
          // Phase 15.7 — Indented children (e.g. /reports/memos) use exact match
          // so they highlight independently from their parent.
          const isChild = !!n.indent;
          return (
            <NavLink
              key={n.to} to={n.to} end={n.to === '/dashboard' || isChild}
              data-testid={n.testid}
              title={isRail ? n.label : undefined}
              className={({ isActive }) => {
                const parentMatch = !isChild && location.pathname.startsWith(n.to + '/');
                const active = isActive || parentMatch;
                if (isRail) {
                  return `sidebar-link-rail ${active ? 'active' : ''}`;
                }
                return `sidebar-link ${active ? 'active' : ''} ${isChild ? 'ml-6 !py-1.5 text-[12px]' : ''}`;
              }}
              onClick={onNav}
            >
              <n.icon size={isRail ? 18 : (isChild ? 13 : 16)} strokeWidth={1.6} style={n.color ? { color: n.color } : undefined} />
              {!isRail && <span>{n.label}</span>}
              {n.to === '/notifications' && inboxUnread > 0 && (
                isRail ? (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-600 border border-white" data-testid="sidebar-nav-notifications-dot" />
                ) : (
                  <Badge className="ml-auto bg-rose-600 text-white text-[10px] px-1.5 min-w-[20px] justify-center" data-testid="sidebar-nav-notifications-badge">
                    {inboxUnread > 99 ? '99+' : inboxUnread}
                  </Badge>
                )
              )}
            </NavLink>
          );
        })}

        {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
          <div className={`pt-4 mt-2 border-t border-[var(--divider)]`}>
            {!isRail && (
              <div className="px-3 text-[10px] tracking-widest uppercase text-slate-400 mb-1">Admin</div>
            )}
            {ADMIN_NAV_FOR_ALL_ADMINS.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testid}
                title={isRail ? n.label : undefined}
                className={({ isActive }) => (isRail ? `sidebar-link-rail ${isActive ? 'active' : ''}` : `sidebar-link ${isActive ? 'active' : ''}`)}
                onClick={onNav}>
                <n.icon size={isRail ? 18 : 16} strokeWidth={1.6} />
                {!isRail && <span>{n.label}</span>}
              </NavLink>
            ))}
            {isSuperAdmin && SUPER_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testid}
                title={isRail ? n.label : undefined}
                className={({ isActive }) => (isRail ? `sidebar-link-rail ${isActive ? 'active' : ''}` : `sidebar-link ${isActive ? 'active' : ''}`)}
                onClick={onNav}>
                <n.icon size={isRail ? 18 : 16} strokeWidth={1.6} />
                {!isRail && <span>{n.label}</span>}
              </NavLink>
            ))}
          </div>
        )}
      </nav>
      <div className={`${isRail ? 'px-1.5' : 'px-3'} py-3 border-t border-[var(--divider)]`}>
        {isRail ? (
          // Rail-mode footer: shrink to just avatar + logout button
          // stacked vertically so both actions stay reachable.
          <div className="flex flex-col items-center gap-2">
            <div
              className="w-8 h-8 rounded-full bg-[var(--ink-navy)] text-white flex items-center justify-center text-xs font-medium"
              title={user?.name || user?.email}
              data-testid="sidebar-user-avatar-rail"
            >
              {(user?.name || user?.email || '?').slice(0,1).toUpperCase()}
            </div>
            <button
              onClick={logout}
              data-testid="sidebar-logout-button-rail"
              className="text-slate-500 hover:text-slate-900 p-1.5 rounded-md hover:bg-slate-100"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
            {showCollapseButton && onCollapse && (
              <button
                type="button"
                onClick={onCollapse}
                className="text-slate-400 hover:text-slate-800 p-1.5 rounded-md hover:bg-slate-100"
                data-testid="sidebar-collapse-btn-rail"
                title="Hide sidebar (Ctrl + \\)"
                aria-label="Hide sidebar"
              >
                <PanelLeftClose size={15} />
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-[var(--ink-navy)] text-white flex items-center justify-center text-xs font-medium">
              {(user?.name || user?.email || '?').slice(0,1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate capitalize">{user?.role?.replace('_',' ')}</div>
            </div>
            <button onClick={logout} data-testid="sidebar-logout-button" className="text-slate-500 hover:text-slate-900 p-1.5 rounded-md hover:bg-slate-100" title="Logout">
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wave 3.99x / 3.99y — Collapsible sidebar with hover-peek + Rail mode.
//
// Three modes:
//   • **expanded** (default) — 268-px sticky sidebar with full labels.
//   • **rail** — 64-px icon-only strip. Labels drop to ``title``
//     tooltips so the sidebar stays glanceable while giving the main
//     content ~200 more pixels of horizontal room.
//   • **collapsed** — sidebar animates out (translateX(-100%))
//     leaving a thin 12-px hover zone anchored to the left edge of
//     the viewport. Hover reveals the full sidebar as a floating
//     overlay (peek).
//
// The chevron inside the sidebar cycles expanded → rail → collapsed
// → expanded. The topbar expand button (only visible when collapsed)
// jumps straight back to expanded so power users don't have to hunt
// through modes.
//
// Ctrl / Cmd + \ cycles too (matches VS Code editor sidebar shortcut).
//
// Preference persisted per browser in ``localStorage`` under
// ``appshell.sidebar.mode``. Legacy boolean ``appshell.sidebar.collapsed``
// is silently migrated on read.
// ---------------------------------------------------------------------------
const _SIDEBAR_MODES = ['expanded', 'rail', 'collapsed'];
function useSidebarMode() {
  const [mode, setMode] = useState(() => {
    try {
      const v = localStorage.getItem('appshell.sidebar.mode');
      if (v && _SIDEBAR_MODES.includes(v)) return v;
      // Legacy migration — Wave 3.99x users on the boolean key.
      const legacy = localStorage.getItem('appshell.sidebar.collapsed');
      if (legacy === '1') return 'collapsed';
      return 'expanded';
    } catch { return 'expanded'; }
  });
  const setPersisted = (v) => {
    const next = _SIDEBAR_MODES.includes(v) ? v : 'expanded';
    setMode(next);
    try {
      localStorage.setItem('appshell.sidebar.mode', next);
      localStorage.setItem('appshell.sidebar.collapsed', next === 'collapsed' ? '1' : '0');
    } catch { /* noop */ }
  };
  return [mode, setPersisted];
}

// Reflect the current sidebar mode onto <html> so global CSS (see
// index.css / App.css) can react — e.g. widen grids on Order Forms
// when the sidebar is rail or collapsed.
function useSyncSidebarModeAttr(mode) {
  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-sidebar-mode', mode);
    } catch { /* noop */ }
  }, [mode]);
}

export default function AppShell({ children, title, subtitle, right }) {
  const { isSuperAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [kbdOpen, setKbdOpen] = useState(false);
  const [mode, setMode] = useSidebarMode();
  const [peek, setPeek] = useState(false);
  const location = useLocation();
  const peekTimerRef = useRef(null);

  // Publish the mode to <html> so global CSS can hook into it.
  useSyncSidebarModeAttr(mode);

  // Route change → dismiss transient peek so the sidebar doesn't
  // stay hovered over a fresh page.
  useEffect(() => { setPeek(false); }, [location.pathname]);

  // Ctrl/Cmd + \ cycles the sidebar mode. Order: expanded → rail →
  // collapsed → expanded.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '\\') {
        e.preventDefault();
        const idx = _SIDEBAR_MODES.indexOf(mode);
        setMode(_SIDEBAR_MODES[(idx + 1) % _SIDEBAR_MODES.length]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, setMode]);

  const openPeek = () => {
    if (peekTimerRef.current) { clearTimeout(peekTimerRef.current); peekTimerRef.current = null; }
    setPeek(true);
  };
  const closePeek = () => {
    // Small delay so a mouse crossing the small edge zone briefly
    // doesn't flicker the sidebar in-and-out.
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => setPeek(false), 120);
  };

  const collapsed = mode === 'collapsed';
  const rail = mode === 'rail';

  // Desktop-only sidebar visibility. Sidebar is "visible" if:
  //   • mode !== 'collapsed' (expanded/rail always show inline), OR
  //   • collapsed AND peeking (floats as overlay above content).
  const sidebarIsOverlay = collapsed && peek;

  // Cycle handler wired into the in-sidebar chevron.
  const cycleForward = () => {
    const idx = _SIDEBAR_MODES.indexOf(mode);
    const next = _SIDEBAR_MODES[(idx + 1) % _SIDEBAR_MODES.length];
    setMode(next);
    if (next !== 'collapsed') setPeek(false);
  };

  return (
    <div className="min-h-screen flex bg-[var(--paper-warm)]">
      {/* Wave 2.1 — Global keyboard shortcut listener (G + key) */}
      <KeyboardShortcuts onShowHelp={() => setKbdOpen(true)} />
      <KeyboardShortcutsDialog open={kbdOpen} onOpenChange={setKbdOpen} />
      {/* Command Palette — global Ctrl/Cmd+K quick launcher */}
      <CommandPalette />

      {/* Desktop sidebar — collapsible with hover-peek + rail. */}
      <aside
        onMouseEnter={sidebarIsOverlay ? openPeek : undefined}
        onMouseLeave={sidebarIsOverlay ? closePeek : undefined}
        data-testid="app-sidebar"
        data-mode={mode}
        data-peek={peek ? '1' : '0'}
        className={[
          'hidden md:flex shrink-0 border-r border-[var(--divider)] bg-white',
          // Width is fluid per mode — rail is 64 px, expanded is
          // 268 px. Collapsed keeps 268 px because we transform it
          // off-canvas and want the peek overlay at the full width.
          rail ? 'w-[64px]' : 'w-[268px]',
          // Transition width alongside transform so mode changes
          // feel like a single continuous animation.
          'transition-[transform,box-shadow,width] duration-200 ease-out',
          sidebarIsOverlay
            ? 'fixed top-0 left-0 h-screen z-40 shadow-[0_10px_40px_-10px_rgba(15,23,42,0.25)]'
            : 'sticky top-0 h-screen self-start',
          collapsed && !peek ? '-translate-x-full' : 'translate-x-0',
        ].join(' ')}
      >
        <SidebarContent
          mode={rail ? 'rail' : 'expanded'}
          onCollapse={cycleForward}
          showCollapseButton={true}
        />
      </aside>

      {/* Left-edge hover zone. Only active when the sidebar is
          fully collapsed. */}
      {collapsed && (
        <div
          onMouseEnter={openPeek}
          onMouseLeave={closePeek}
          className="hidden md:block fixed left-0 top-0 h-screen w-2.5 z-30 cursor-pointer"
          aria-hidden="true"
          data-testid="app-sidebar-hover-zone"
        />
      )}

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[280px] bg-white">
          <SidebarContent mode="expanded" onNav={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-14 border-b border-[var(--divider)] bg-white/90 backdrop-blur sticky top-0 z-30 flex items-center px-4 md:px-6 gap-3">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu size={18} />
          </Button>
          {/* Wave 3.99x/y — Expand-sidebar affordance in the topbar.
              Surfaces on desktop when the sidebar is collapsed AND
              also gives a one-click restore-to-expanded even from
              rail mode. */}
          {(collapsed || rail) && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:inline-flex"
              onClick={() => { setMode('expanded'); setPeek(false); }}
              data-testid="sidebar-expand-btn"
              title="Show full sidebar (Ctrl + \\ cycles)"
              aria-label="Show full sidebar"
            >
              <PanelLeftOpen size={18} />
            </Button>
          )}
          <div className="flex-1">
            {title && <div className="text-[15px] font-medium text-[var(--ink-navy)]" data-testid="page-title">{title}</div>}
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
          {isSuperAdmin && <NotesModuleSwitch />}
          <CompanySwitcher />
          <DelayedOrdersBadge />
          <DensityToggle />
          <KeyboardShortcutsButton onOpen={() => setKbdOpen(true)} />
          {right}
        </header>

        <div className="flex-1 px-4 md:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>

      {/* Right-hand Team Presence rail (Super Admin + Company Admin only).
          Collapses horizontally like the left sidebar to reclaim width. */}
      <TeamPresencePanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes module switch — Super Admin only. A segmented control in the top
// header that keeps Inventique active and jumps to the isolated Notes
// workspace (/notes). The Notes module has its own auth context; a Super
// Admin's Inventique token is accepted by the Notes backend so no second
// login is required.
// ---------------------------------------------------------------------------
function NotesModuleSwitch() {
  const navigate = useNavigate();
  return (
    <div
      className="hidden sm:inline-flex items-center rounded-lg border border-[var(--divider)] bg-white p-0.5"
      role="tablist"
      data-testid="superadmin-module-switch-tabs"
    >
      <span
        role="tab"
        aria-selected="true"
        data-testid="superadmin-module-switch-inventique"
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium bg-[var(--ink-navy)] text-white"
      >
        <LayoutDashboard size={13} /> Inventique
      </span>
      <button
        type="button"
        role="tab"
        aria-selected="false"
        onClick={() => navigate('/notes')}
        title="Open Notes workspace"
        data-testid="superadmin-module-switch-notes"
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
      >
        <NotebookPen size={13} /> Notes
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wave 3.78 — Top-nav "Delayed Orders" badge.
// Polls the production-timeline delayed-count endpoint every 60s and
// links to /production-timeline?filter=delayed on click. Hidden when
// there is no company scope (e.g. Super Admin without a chosen company).
// ---------------------------------------------------------------------------
function DelayedOrdersBadge() {
  const navigate = useNavigate();
  const { effectiveCompanyId, isSalesRole } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!effectiveCompanyId || isSalesRole) { setCount(0); return; }
    const load = async () => {
      try {
        const { data } = await api.get('/production-timeline/delayed-count', { params: { company_id: effectiveCompanyId } });
        if (!cancelled) setCount(Number(data?.delayed || 0));
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [effectiveCompanyId]);

  if (!effectiveCompanyId || isSalesRole) return null;
  const tone = count > 0
    ? { bg: '#FEE2E2', text: '#B91C1C', border: '#FCA5A5' }
    : { bg: '#EAF7F5', text: '#0F766E', border: '#B7E4DA' };

  return (
    <button
      type="button"
      onClick={() => navigate('/production-timeline?filter=delayed')}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12px] font-medium border transition-colors duration-150"
      style={{ background: tone.bg, color: tone.text, borderColor: tone.border }}
      title={count > 0 ? `${count} delayed order${count === 1 ? '' : 's'} — open Production Timeline` : 'No delays — Production Timeline'}
      data-testid="topbar-delayed-orders-badge"
    >
      {count > 0 ? <AlertTriangle size={13} /> : <Timer size={13} />}
      <span className="font-mono tabular-nums">{count}</span>
      <span className="hidden md:inline">Delayed</span>
    </button>
  );
}
